import { useState } from 'react';

import { ConfirmationMethodSchema, fmtDate } from '@dcc-erp/shared';
import type { ConfirmationMethodValue, VendorBankChange } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { HttpError } from '../../lib/api';
import {
  useApproveVendorBankChangeStep,
  useConfirmVendorBankChange,
  useRejectVendorBankChange,
  useReturnVendorBankChange,
  useSubmitVendorBankChange,
  useVendorBankChanges,
} from '../../lib/queries/suppliers';
import { AppModal } from '../AppModal';
import { ApprovalTimeline, type ApprovalTimelineRow } from '../approval/ApprovalTimeline';
import { DecisionModal, type Decision } from '../approval/DecisionModal';
import { Can } from '../Can';

interface Props {
  companyId: string;
  supplierId: string;
  vendorId: string;
  request: VendorBankChange;
  onClose: () => void;
}

const CONFIRM_METHODS = ConfirmationMethodSchema.options;
const CONFIRM_LABEL: Record<ConfirmationMethodValue, string> = {
  PHONE: 'โทรศัพท์',
  LETTER: 'หนังสือ',
  EMAIL_TO_VENDOR: 'อีเมลถึงผู้ขาย',
};

// One old→new bank diff field. Highlights when the value actually changed — the
// redirection target the approver must scrutinize.
function DiffField({
  label,
  oldValue,
  newValue,
}: {
  label: string;
  oldValue: string | null | undefined;
  newValue: string | null | undefined;
}) {
  const changed = (newValue ?? null) !== null && (oldValue ?? null) !== (newValue ?? null);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr', gap: 8, fontSize: 13 }}>
      <div className="muted">{label}</div>
      <div className="code" style={{ textDecoration: changed ? 'line-through' : 'none' }}>
        {oldValue ?? '—'}
      </div>
      <div
        className="code"
        style={{ fontWeight: changed ? 700 : 400, color: changed ? 'var(--brand)' : undefined }}
      >
        {newValue ?? '—'}
      </div>
    </div>
  );
}

// VendorBankChangeApprovalPanel — VBC 4-eyes (docType VBC, DECISION-Z). Mirrors
// ApPaymentApprovalPanel: the maker submits (vendorbank.request); an approver
// records the out-of-band attestation (D3, vendorbank.approve) then decides
// (vendorbank.approve). The "พิจารณา" button stays disabled until the attestation
// is recorded (confirmedAt) — mirroring the server gate. On final approve the
// server writes the Vendor bank columns + SupplierChangeLog (APPLIED).
export function VendorBankChangeApprovalPanel({
  companyId,
  supplierId,
  vendorId,
  request,
  onClose,
}: Props) {
  // The list is the live source — re-read it so the panel reflects a just-recorded
  // attestation / decision without a prop round-trip.
  const { data: list } = useVendorBankChanges(companyId, supplierId, vendorId);
  const view = list?.find((r) => r.id === request.id) ?? request;

  const submitMut = useSubmitVendorBankChange(companyId, supplierId, vendorId);
  const confirmMut = useConfirmVendorBankChange(companyId, supplierId, vendorId);
  const approveMut = useApproveVendorBankChangeStep(companyId, supplierId, vendorId);
  const rejectMut = useRejectVendorBankChange(companyId, supplierId, vendorId);
  const returnMut = useReturnVendorBankChange(companyId, supplierId, vendorId);

  const [showDecide, setShowDecide] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [method, setMethod] = useState<ConfirmationMethodValue>('PHONE');
  const [contact, setContact] = useState('');

  const decideBusy = approveMut.isPending || rejectMut.isPending || returnMut.isPending;
  const submitBusy = submitMut.isPending;
  const confirmBusy = confirmMut.isPending;

  const approvalStatus = view.approvalStatus ?? null;
  const currentStep = view.currentStep ?? 0;
  const approvals = view.approvals ?? [];
  const totalSteps = approvals.length;
  const isPending = approvalStatus === 'PENDING';
  const isResubmittable = approvalStatus === 'DRAFT' || approvalStatus === 'RETURNED';
  const confirmed = !!view.confirmedAt;

  const timelineRows: ApprovalTimelineRow[] = approvals.map((a) => ({
    id: a.stepNo,
    stepNo: a.stepNo,
    roleLabel: a.roleLabel,
    approvedBy: a.approvedBy,
    comment: a.comment,
    approvedAt: a.approvedAt,
  }));

  async function handleSubmit() {
    setActionError(null);
    try {
      await submitMut.mutateAsync(view.id);
      onClose();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  async function handleConfirm() {
    setActionError(null);
    try {
      await confirmMut.mutateAsync({
        reqId: view.id,
        data: { confirmationMethod: method, confirmedContact: contact.trim() },
      });
    } catch (e) {
      surfaceError(e);
    }
  }

  function surfaceError(e: unknown) {
    if (e instanceof HttpError && (e.status === 403 || e.status === 400)) {
      // 403 = conflict-of-interest / self-confirm forbidden; 400 = attestation gate.
      setActionError(e.message);
    } else {
      setActionError(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  async function handleDecide(d: Decision) {
    setActionError(null);
    try {
      if (d.action === 'approve') {
        await approveMut.mutateAsync({
          reqId: view.id,
          step: d.step,
          comment: d.comment,
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        });
      } else if (d.action === 'return') {
        await returnMut.mutateAsync({
          reqId: view.id,
          step: d.step,
          reason: d.reason,
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        });
      } else {
        await rejectMut.mutateAsync({
          reqId: view.id,
          step: d.step,
          reason: d.reason,
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        });
      }
      setShowDecide(false);
      onClose();
    } catch (e) {
      surfaceError(e);
    }
  }

  return (
    <AppModal
      title={`อนุมัติเปลี่ยนบัญชีธนาคาร — ${view.voucherNo}`}
      {...(view.reason ? { subtitle: view.reason } : {})}
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            ปิด
          </button>
          {isResubmittable && (
            <Can permission="vendorbank.request">
              <button
                className="btn primary"
                disabled={submitBusy}
                data-testid="vbc-submit"
                onClick={() => {
                  void handleSubmit();
                }}
              >
                {submitBusy ? 'กำลังส่ง…' : 'ส่งอนุมัติ'}
              </button>
            </Can>
          )}
          {isPending && (
            <Can permission="vendorbank.approve">
              <button
                className="btn primary"
                disabled={decideBusy || !confirmed}
                data-testid="vbc-decide"
                title={confirmed ? undefined : 'ต้องยืนยันบัญชีกับผู้ขายก่อนจึงจะพิจารณาได้'}
                onClick={() => setShowDecide(true)}
              >
                พิจารณา
              </button>
            </Can>
          )}
        </>
      }
    >
      {/* Old → new bank diff (the redirection target — surfaced prominently). */}
      <div
        style={{
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: 12,
          marginBottom: 12,
          display: 'grid',
          gap: 6,
        }}
        data-testid="vbc-bank-diff"
      >
        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr', gap: 8 }}>
          <div className="meta" />
          <div className="meta">เดิม</div>
          <div className="meta">ใหม่</div>
        </div>
        <DiffField
          label="เลขบัญชี"
          oldValue={view.oldBankAccountNumber}
          newValue={view.newBankAccountNumber}
        />
        <DiffField label="ธนาคาร" oldValue={view.oldBankCode} newValue={view.newBankCode} />
        <DiffField
          label="รหัสสาขา"
          oldValue={view.oldBankBranchCode}
          newValue={view.newBankBranchCode}
        />
        <DiffField
          label="ชื่อบัญชี"
          oldValue={view.oldAccountHolderName}
          newValue={view.newAccountHolderName}
        />
        <DiffField
          label="ประเภท"
          oldValue={view.oldBankAccountType}
          newValue={view.newBankAccountType}
        />
      </div>

      {approvalStatus === 'RETURNED' && view.returnedReason && (
        <div
          style={{
            background: 'var(--warning-bg)',
            border: '1px solid var(--warning)',
            borderRadius: 6,
            padding: '10px 14px',
            fontSize: 13,
            marginBottom: 12,
          }}
          data-testid="vbc-returned-reason"
        >
          <strong style={{ color: 'var(--warning)' }}>เหตุผลที่ส่งกลับ:</strong>{' '}
          {view.returnedReason}
        </div>
      )}

      {isPending && (
        <div className="meta" style={{ marginBottom: 12 }}>
          <Icon name="check" size={12} /> รออนุมัติ · ขั้น {currentStep}/{totalSteps}
        </div>
      )}

      {/* Attestation sub-form (D3) — visible to an approver while PENDING. The
          attester must NOT be the requester (server enforces); recording it
          unlocks the พิจารณา button. */}
      {isPending && (
        <Can permission="vendorbank.approve">
          <div
            style={{
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: 12,
              marginBottom: 12,
              display: 'grid',
              gap: 8,
            }}
            data-testid="vbc-attestation"
          >
            <div className="meta">
              {confirmed
                ? `ยืนยันบัญชีแล้ว · ${view.confirmedContact ?? ''}${
                    view.confirmedAt ? ` · ${fmtDate(view.confirmedAt)}` : ''
                  }`
                : 'ยืนยันบัญชีกับผู้ขาย (out-of-band) ก่อนพิจารณา'}
            </div>
            {!confirmed && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div>
                  <label className="meta" style={{ display: 'block', marginBottom: 4 }}>
                    ช่องทาง
                  </label>
                  <select
                    className="input"
                    data-testid="vbc-method"
                    value={method}
                    onChange={(e) => setMethod(e.target.value as ConfirmationMethodValue)}
                  >
                    {CONFIRM_METHODS.map((m) => (
                      <option key={m} value={m}>
                        {CONFIRM_LABEL[m]}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <label className="meta" style={{ display: 'block', marginBottom: 4 }}>
                    ผู้ติดต่อ/เบอร์ที่ยืนยัน
                  </label>
                  <input
                    className="input"
                    data-testid="vbc-confirm"
                    value={contact}
                    onChange={(e) => setContact(e.target.value)}
                    style={{ width: '100%' }}
                  />
                </div>
                <button
                  type="button"
                  className="btn"
                  disabled={confirmBusy || contact.trim().length === 0}
                  onClick={() => void handleConfirm()}
                >
                  {confirmBusy ? 'กำลังบันทึก…' : 'ยืนยันบัญชี'}
                </button>
              </div>
            )}
          </div>
        </Can>
      )}

      <ApprovalTimeline
        approvals={timelineRows}
        isActive={isPending}
        currentStep={currentStep}
        emptyText="ยังไม่ส่งอนุมัติ"
        leadLine={
          view.submittedBy ? (
            <div className="meta">
              ส่งอนุมัติโดย {view.submittedBy}
              {view.submittedAt ? ` · ${fmtDate(view.submittedAt)}` : ''}
            </div>
          ) : undefined
        }
        trailLine={
          approvalStatus === 'APPROVED' && view.approvedBy ? (
            <div className="meta">
              อนุมัติโดย {view.approvedBy}
              {view.approvedAt ? ` · ${fmtDate(view.approvedAt)}` : ''}
            </div>
          ) : undefined
        }
      />

      {actionError && (
        <div
          style={{ color: 'var(--negative)', fontSize: 14, marginTop: 10 }}
          data-testid="vbc-action-error"
        >
          {actionError}
        </div>
      )}

      {showDecide && (
        <DecisionModal
          voucherNo={view.voucherNo}
          step={currentStep || 1}
          busy={decideBusy}
          error={actionError}
          titlePrefix="พิจารณาเปลี่ยนบัญชีธนาคาร"
          onConfirm={(d) => {
            void handleDecide(d);
          }}
          onClose={() => {
            setShowDecide(false);
            setActionError(null);
          }}
        />
      )}
    </AppModal>
  );
}
